import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import PlantApi from "../../services/PlantApi";
import UserApi from "../../services/UserApi";
import PlantUserApi from "../../services/PlantUserApi";
import FilterPanel from "../../components/FilterPanel/FilterPanel";
import Input from "../../components/Input/Input";
import DataTable from "../../components/DataTable/DataTable";
import FormCard from "../../components/FormCard/FormCard";
import styles from "./index.module.css";

interface PlantRow {
  id: string;
  name: string;
  companyName?: string;
  isActive?: boolean;
  isDefault?: boolean;
  isLinked?: boolean;
}

const extractList = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.Items)) return payload.Items;
  if (Array.isArray(payload?.result)) return payload.result;
  return [];
};

const normalizePlant = (entry: any): PlantRow | null => {
  const id = entry?.id ?? entry?.plantId ?? entry?.Id;
  if (!id) {
    return null;
  }

  return {
    id,
    name: entry?.name ?? entry?.plantName ?? entry?.Name ?? "",
    companyName:
      entry?.company?.name ??
      entry?.companyName ??
      entry?.CompanyName ??
      entry?.company?.Name ??
      "",
    isActive:
      entry?.active ??
      entry?.isActive ??
      entry?.Active ??
      (typeof entry?.status === "string" ? entry.status.toLowerCase() === "active" : undefined),
    isDefault: entry?.isDefault ?? false,
  };
};

interface UserPlantLink {
  plantId: string;
  isDefault?: boolean;
}

const normalizeUserPlantLink = (entry: any): UserPlantLink | null => {
  const plantId = entry?.plantId ?? entry?.PlantId ?? entry?.id ?? entry?.Id;
  if (!plantId) {
    return null;
  }

  return {
    plantId,
    isDefault: entry?.isDefault ?? entry?.IsDefault ?? false,
  };
};

const normalizeUserName = (payload: any): string => {
  if (!payload) {
    return "";
  }

  return (
    payload?.name ??
    payload?.Name ??
    payload?.fullName ??
    payload?.FullName ??
    payload?.username ??
    payload?.Username ??
    ""
  );
};

const textIncludes = (haystack: string | undefined, needle: string | undefined): boolean => {
  if (!needle) {
    return true;
  }

  const safeHaystack = (haystack ?? "").toLocaleLowerCase();
  const safeNeedle = needle.toLocaleLowerCase();
  return safeHaystack.includes(safeNeedle);
};

const sortList = (
  list: PlantRow[],
  field: string,
  direction: "asc" | "desc",
): PlantRow[] => {
  if (!field) {
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }

  const sorted = [...list].sort((a, b) => {
    const aValue = (a as Record<string, any>)[field] ?? "";
    const bValue = (b as Record<string, any>)[field] ?? "";
    return String(aValue).localeCompare(String(bValue));
  });

  return direction === "desc" ? sorted.reverse() : sorted;
};

const filterList = (list: PlantRow[], filters: Record<string, any>): PlantRow[] => {
  if (!filters || Object.keys(filters).length === 0) {
    return list;
  }

  const nameFilter = filters.name?.value as string | undefined;
  const companyFilter = filters.companyName?.value as string | undefined;
  const defaultFilter = filters.isDefault?.value;

  return list.filter((plant) => {
    const matchesName = textIncludes(plant.name, nameFilter);
    const matchesCompany = textIncludes(plant.companyName, companyFilter);
    const matchesDefault = Array.isArray(defaultFilter)
      ? defaultFilter.length === 0 || defaultFilter.includes(Boolean(plant.isDefault))
      : typeof defaultFilter === "boolean"
        ? Boolean(plant.isDefault) === defaultFilter
        : true;
    return matchesName && matchesCompany && matchesDefault;
  });
};

const LinkUserPlant: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(["form", "General"]);
  const { displayToast } = useToast();
  const { companyId: currentCompanyId } = useAuth();

  const [userName, setUserName] = useState<string>("");
  const [allPlants, setAllPlants] = useState<PlantRow[]>([]);
  const [plants, setPlants] = useState<PlantRow[]>([]);
  const [initialSelectedIds, setInitialSelectedIds] = useState<string[]>([]);
  const [selectedPlants, setSelectedPlants] = useState<PlantRow[]>([]);
  const [defaultPlantId, setDefaultPlantId] = useState<string>("");
  const [initialDefaultId, setInitialDefaultId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [savedFilters, setSavedFilters] = useState<Record<string, any>>({});

  const linkPlantsHeader = useMemo(
    () => t("linkPlantsHeader", { defaultValue: "Link Plants to User" }),
    [t],
  ); 

  const normalizeId = (value: string | undefined | null): string =>
    typeof value === "string" ? value.trim().toLocaleLowerCase() : "";

  const markDefaultInList = (list: PlantRow[], defaultId: string): PlantRow[] => {
    const normalizedDefault = normalizeId(defaultId);
    return list.map((plant) => ({
      ...plant,
      isDefault: normalizedDefault.length > 0 && normalizeId(plant.id) === normalizedDefault,
    }));
  };

  const updateLinkStatus = (list: PlantRow[], plantId: string, isLinked: boolean): PlantRow[] => {
    const targetId = normalizeId(plantId);
    if (!targetId) {
      return list;
    }

    return list.map((plant) => {
      if (normalizeId(plant.id) !== targetId) {
        return plant;
      }

      return {
        ...plant,
        isLinked,
      };
    });
  };

  const rebuildPlants = (
    source: PlantRow[],
    filters: Record<string, any>,
    firstIndex: number,
    field: string,
    direction: "asc" | "desc",
    pageSize: number,
  ): void => {
    const sanitizedField = field || "name";
    const sanitizedDirection = direction || "asc";
    const filtered = filterList(source, filters);
    const sorted = sortList(filtered, sanitizedField, sanitizedDirection);
    const slice = sorted.slice(firstIndex, firstIndex + pageSize);
    setTotalRecords(sorted.length);
    setPlants(slice);
  };

  useEffect(() => {
    if (!userId) {
      return;
    }

    const bootstrap = async (): Promise<void> => {
      setLoading(true);
      try {
        const userResponse = await UserApi.GetById(userId);
        const userPayload = userResponse?.data ?? userResponse;
        const resolvedUserName = normalizeUserName(userPayload);
        const companyIdFromToken = (currentCompanyId ?? "").trim();

        if (!companyIdFromToken) {
          setAllPlants([]);
          setPlants([]);
          setInitialSelectedIds([]);
          setSelectedPlants([]);
          setUserName(resolvedUserName);
          setTotalRecords(0);
          displayToast(
            "error",
            t("error", { defaultValue: "Error" }) as string,
            t("CompanyNotFound", { defaultValue: "Company not found for current session." }) as string,
            false,
            5000,
          );
          throw new Error("Missing companyId in auth context");
        }

        const [plantsResponse, linkedResponse] = await Promise.all([
          PlantApi.GetPlantsByCompanyId(companyIdFromToken),
          PlantUserApi.GetPlantsByUser(userId),
        ]);

        const rawPlants = extractList(plantsResponse?.data ?? plantsResponse);
        const normalizedPlants = rawPlants
          .map((entry: any) => normalizePlant(entry))
          .filter((entry): entry is PlantRow => Boolean(entry));

        const activePlants = normalizedPlants.filter((plant) => plant.isActive !== false);

        const userPlantLinks = extractList(linkedResponse?.data ?? linkedResponse)
          .map((item: any) => normalizeUserPlantLink(item))
          .filter((entry): entry is UserPlantLink => Boolean(entry));

        const linkedIdMap = new Map<string, boolean>();
        const linkedIdSet = new Set<string>();
        userPlantLinks.forEach((link) => {
          const normalizedId = (link.plantId ?? "").toLowerCase();
          if (normalizedId.length === 0) {
            return;
          }
          linkedIdMap.set(normalizedId, Boolean(link.isDefault));
          linkedIdSet.add(normalizedId);
        });

        const decoratedPlants = activePlants
          .filter((plant) => {
            const normalizedId = (plant.id ?? "").toLowerCase();
            return normalizedId.length > 0;
          })
          .map((plant) => {
            const normalizedId = (plant.id ?? "").toLowerCase();
            const isLinked = linkedIdSet.has(normalizedId);
            const isDefault = linkedIdMap.get(normalizedId) ?? plant.isDefault ?? false;
            return {
              ...plant,
              isDefault,
              isLinked,
            } as PlantRow & { isLinked: boolean };
          });

        const finalPlants = decoratedPlants
          .sort((a, b) =>
          a.name.localeCompare(b.name),
        );

        const resolvedSelected = finalPlants.filter((plant) => plant.isLinked);

        let resolvedDefaultId = resolvedSelected.find((plant) => plant.isDefault)?.id ?? "";
        if (!resolvedDefaultId && resolvedSelected.length > 0) {
          resolvedDefaultId = resolvedSelected[0]?.id ?? "";
        }

        const trimmedResolvedDefaultId = typeof resolvedDefaultId === "string"
          ? resolvedDefaultId.trim()
          : "";

        const adjustedAllPlants = markDefaultInList(finalPlants, trimmedResolvedDefaultId);
        const adjustedSelectedPlants = markDefaultInList(resolvedSelected, trimmedResolvedDefaultId);

        setAllPlants(adjustedAllPlants);
        setInitialSelectedIds(adjustedSelectedPlants.map((plant) => plant.id));
        setSelectedPlants(adjustedSelectedPlants);
        setDefaultPlantId(trimmedResolvedDefaultId);
        setInitialDefaultId(trimmedResolvedDefaultId);
        setUserName(resolvedUserName);
        setSavedFilters({});
        setPage(0);
        rebuildPlants(adjustedAllPlants, {}, 0, sortField, sortOrder, rowsPerPage);
      } catch (err: any) {
        const detail = err?.message ?? "Error";
        displayToast("error", t("error") as string, detail, false, 5000);
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, currentCompanyId, displayToast, t]);

  const handleCancel = (): void => {
    navigate(-1);
  };

  const handlePlantSelectionChange = (plant: PlantRow, shouldSelect: boolean): void => {
    const plantId = plant?.id ?? "";
    if (!plantId) {
      return;
    }

    const normalizedTargetId = normalizeId(plantId);
    const isSelected = selectedPlants.some((item) => normalizeId(item.id) === normalizedTargetId);

    if (shouldSelect) {
      if (isSelected) {
        return;
      }

      const nextSelectedRaw = [...selectedPlants, { ...plant, isLinked: true }];
      const nextDefaultId = (defaultPlantId || plantId || "").trim();
      const updatedSelected = markDefaultInList(nextSelectedRaw, nextDefaultId);
      let updatedAll = updateLinkStatus(allPlants, plantId, true);
      updatedAll = markDefaultInList(updatedAll, nextDefaultId);

      setSelectedPlants(updatedSelected);
      setAllPlants(updatedAll);
      setDefaultPlantId(nextDefaultId);
      rebuildPlants(updatedAll, savedFilters, page, sortField, sortOrder, rowsPerPage);
      return;
    }

    if (!isSelected) {
      return;
    }

    if (selectedPlants.length <= 1) {
      displayToast(
        "warn",
        t("attention", { defaultValue: "Attention" }) as string,
        t("atLeastOnePlant", { defaultValue: "At least one plant must remain selected." }) as string,
        false,
        4000,
      );
      return;
    }

    const nextSelectedRaw = selectedPlants.filter(
      (item) => normalizeId(item.id) !== normalizedTargetId,
    );
    const nextDefaultId =
      normalizeId(defaultPlantId) === normalizedTargetId
        ? (nextSelectedRaw[0]?.id ?? "").trim()
        : defaultPlantId;

    let updatedAll = updateLinkStatus(allPlants, plantId, false);
    updatedAll = markDefaultInList(updatedAll, nextDefaultId);
    const updatedSelected = markDefaultInList(nextSelectedRaw, nextDefaultId);

    setSelectedPlants(updatedSelected);
    setAllPlants(updatedAll);
    setDefaultPlantId(nextDefaultId);
    rebuildPlants(updatedAll, savedFilters, page, sortField, sortOrder, rowsPerPage);
  };

  const handleDefaultSelection = (plant: PlantRow): void => {
    const plantId = plant?.id ?? "";
    if (!plantId) {
      return;
    }

    const normalizedTargetId = normalizeId(plantId);
    const isSelected = selectedPlants.some((item) => normalizeId(item.id) === normalizedTargetId);

    if (!isSelected) {
      return;
    }

    if (normalizeId(defaultPlantId) === normalizedTargetId) {
      return;
    }

    const trimmedId = typeof plantId === "string" ? plantId.trim() : "";
    const updatedAll = markDefaultInList(allPlants, trimmedId);
    const updatedSelected = markDefaultInList(selectedPlants, trimmedId);

    setSelectedPlants(updatedSelected);
    setAllPlants(updatedAll);
    setDefaultPlantId(trimmedId);
    rebuildPlants(updatedAll, savedFilters, page, sortField, sortOrder, rowsPerPage);
  };

  const handleSave = async (): Promise<void> => {
    if (!userId) {
      return;
    }

    if (selectedPlants.length === 0) {
      displayToast(
        "warn",
        t("attention", { defaultValue: "Attention" }) as string,
        t("atLeastOnePlant", { defaultValue: "At least one plant must remain selected." }) as string,
        false,
        4000,
      );
      return;
    }

    const trimmedUserId = (userId ?? "").trim();
    if (!trimmedUserId) {
      displayToast(
        "error",
        t("error", { defaultValue: "Error" }) as string,
        t("userNotFound", { defaultValue: "User identifier not available." }) as string,
        false,
        4000,
      );
      return;
    }
    const normalizedDefault = normalizeId(defaultPlantId);
    if (!normalizedDefault) {
      displayToast(
        "warn",
        t("attention", { defaultValue: "Attention" }) as string,
        t("defaultPlantRequired", { defaultValue: "Select a default plant before saving." }) as string,
        false,
        4000,
      );
      return;
    }

    const currentSelectedIds = selectedPlants.map((plant) => plant.id);
    const normalizedCurrentIds = currentSelectedIds.map((id) => normalizeId(id)).filter((id) => id.length > 0).sort();
    const normalizedInitialIds = initialSelectedIds.map((id) => normalizeId(id)).filter((id) => id.length > 0).sort();
    const normalizedInitialDefault = normalizeId(initialDefaultId);
    const defaultChanged = normalizedInitialDefault !== normalizedDefault;

    if (JSON.stringify(normalizedCurrentIds) === JSON.stringify(normalizedInitialIds) && !defaultChanged) {
      return;
    }

    const requestsMap = new Map<string, { userId: string; plantId: string; isDefault: boolean }>();
    selectedPlants.forEach((plant) => {
      const rawPlantId = plant?.id ?? "";
      const trimmedPlantId = typeof rawPlantId === "string" ? rawPlantId.trim() : "";
      const normalizedIdValue = normalizeId(trimmedPlantId);
      if (!normalizedIdValue) {
        return;
      }
      requestsMap.set(normalizedIdValue, {
        userId: trimmedUserId,
        plantId: trimmedPlantId,
        isDefault: normalizedIdValue === normalizedDefault,
      });
    });
    const requests = Array.from(requestsMap.values());
    if (requests.length === 0) {
      displayToast(
        "error",
        t("error", { defaultValue: "Error" }) as string,
        t("PlantSelectionInvalid", { defaultValue: "Unable to resolve selected plants." }) as string,
        false,
        4000,
      );
      return;
    }
    if (!requests.some((request) => request.isDefault)) {
      displayToast(
        "error",
        t("error", { defaultValue: "Error" }) as string,
        t("defaultPlantRequired", { defaultValue: "Select a default plant before saving." }) as string,
        false,
        4000,
      );
      return;
    }

    setSaving(true);
    const removalIds = Array.from(
      new Set(
        initialSelectedIds
          .map((id) => (typeof id === "string" ? id.trim() : ""))
          .filter((id) => id.length > 0),
      ),
    );
    const initialRequestsMap = new Map<string, { userId: string; plantId: string; isDefault: boolean }>();
    initialSelectedIds.forEach((id) => {
      const trimmedId = (id ?? "").trim();
      const normalizedIdValue = normalizeId(trimmedId);
      if (!normalizedIdValue) {
        return;
      }
      initialRequestsMap.set(normalizedIdValue, {
        userId: trimmedUserId,
        plantId: trimmedId,
        isDefault: normalizedIdValue === normalizedInitialDefault,
      });
    });
    const initialRequests = Array.from(initialRequestsMap.values());

    let removalCompleted = false;
    try {
      if (removalIds.length > 0) {
        await PlantUserApi.RemovePlants({ userId, plantIds: removalIds });
        removalCompleted = true;
      }
      await PlantUserApi.AssignPlants(requests);

      displayToast(
        "success",
        t("Success"),
        t("PlantsLinkUpdated", { defaultValue: "Plant links updated successfully" }) as string,
        false,
        3000,
      );
      navigate("/users");
    } catch (err: any) {
      if (removalCompleted && initialRequests.length > 0) {
        try {
          await PlantUserApi.AssignPlants(initialRequests);
        } catch (restoreErr) {
          console.error("Failed to restore previous plant links", restoreErr);
        }
      }
      const msg = err?.response?.data?.data ?? err?.message ?? "Error";
      displayToast("error", t("error") as string, msg, false, 7000);
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = (): boolean => {
    const currentIds = selectedPlants
      .map((plant) => normalizeId(plant.id))
      .filter((id) => id.length > 0)
      .sort();
    const initialIds = initialSelectedIds
      .map((id) => normalizeId(id))
      .filter((id) => id.length > 0)
      .sort();
    if (JSON.stringify(currentIds) !== JSON.stringify(initialIds)) {
      return true;
    }

    return normalizeId(defaultPlantId) !== normalizeId(initialDefaultId);
  };

  const handleFilterChange = (field: string, val: any): void => {
    const nextFilters = { ...savedFilters, [field]: { value: val } };
    setSavedFilters(nextFilters);
  };

  const handleApplyFilters = (): void => {
    setPage(0);
    rebuildPlants(allPlants, savedFilters, 0, sortField, sortOrder, rowsPerPage);
  };

  const handleClearFilters = (): void => {
    setPage(0);
    setSavedFilters({});
    rebuildPlants(allPlants, {}, 0, sortField, sortOrder, rowsPerPage);
  };

  return (
    <FormCard
      title={`${linkPlantsHeader} ${userName ? `- ${userName}` : ""}`}
      description={t("linkUserPlantDescription") || "Vincular plantas ao usuário e definir a planta padrão para o acesso"}
      icon="link"
      onSubmit={handleSave}
      onCancel={handleCancel}
      cancelButtonLabel={t("Cancel") || "Voltar"}
      submitButtonLabel={t("save") || "Salvar"}
      submitDisabled={!hasChanges() || saving}
      loading={saving}
      numColumns={1}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <FilterPanel
          title={t("filter") || "Filtros"}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
        >
          <Input
            type="text"
            label={t("name")}
            placeholder={t("searchByName") || "Pesquisar por nome"}
            value={savedFilters.name?.value || ""}
            onChange={(val) => handleFilterChange("name", val)}
          />
          <Input
            type="text"
            label={t("company")}
            placeholder={t("searchByCompany") || "Pesquisar por empresa"}
            value={savedFilters.companyName?.value || ""}
            onChange={(val) => handleFilterChange("companyName", val)}
          />
          <Input
            type="multiselect"
            label={t("defaultPlant")}
            placeholder={t("selectStatus") || "Selecione o status"}
            value={savedFilters.isDefault?.value || []}
            options={[
              { label: t("yes") || "Sim", value: true },
              { label: t("no") || "Não", value: false },
            ]}
            onChange={(val) => handleFilterChange("isDefault", val)}
          />
        </FilterPanel>

        <DataTable
          value={plants}
          loading={loading}
          totalRecords={totalRecords}
          first={page}
          rows={rowsPerPage}
          paginated
          sortField={sortField}
          sortOrder={sortOrder === "asc" ? 1 : -1}
          onPageChange={(event: any) => {
            setPage(event.first);
            setRowsPerPage(event.rows);
            rebuildPlants(allPlants, savedFilters, event.first, sortField, sortOrder, event.rows);
          }}
          onSort={(event: any) => {
            const nextField = event.sortField || "";
            const nextOrder = event.sortOrder === 1 ? "asc" : "desc";
            setSortField(nextField);
            setSortOrder(nextOrder);
            rebuildPlants(allPlants, savedFilters, page, nextField, nextOrder, rowsPerPage);
          }}
          rowsPerPageOptions={[10, 20, 50]}
          emptyMessage={t("noRecordsFound") || "Nenhum registro encontrado"}
          resizableColumns
        >
          <Column
            field="name"
            header={t("name")}
            sortable
            style={{ minWidth: "18rem" }}
            body={(rowData: PlantRow) => {
              const isSelected = selectedPlants.some(
                (plant) => normalizeId(plant.id) === normalizeId(rowData.id),
              );
              return (
                <span className={isSelected ? styles.selectedTag : undefined}>
                  {rowData.name || "-"}
                </span>
              );
            }}
          />
          <Column
            field="isDefault"
            header={t("defaultPlant") || "Padrão"}
            sortable
            style={{ textAlign: "center", width: "8rem" }}
            body={(rowData: PlantRow) => {
              const isSelected = selectedPlants.some(
                (plant) => normalizeId(plant.id) === normalizeId(rowData.id),
              );
              const isDefault = normalizeId(defaultPlantId) === normalizeId(rowData.id);
              return (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Checkbox
                    inputId={`default-${rowData.id}`}
                    checked={isDefault}
                    disabled={!isSelected}
                    onChange={(event) => {
                      if (!event.checked) {
                        return;
                      }
                      handleDefaultSelection(rowData);
                    }}
                  />
                </div>
              );
            }}
          />
          <Column
            header={t("link")}
            style={{ textAlign: "center", width: "8rem" }}
            body={(rowData: PlantRow) => (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Checkbox
                  inputId={rowData.id}
                  checked={selectedPlants.some(
                    (plant) => normalizeId(plant.id) === normalizeId(rowData.id),
                  )}
                  disabled={
                    selectedPlants.length <= 1 &&
                    selectedPlants.some((plant) => normalizeId(plant.id) === normalizeId(rowData.id))
                  }
                  onChange={(event) => {
                    handlePlantSelectionChange(rowData, Boolean(event.checked));
                  }}
                />
              </div>
            )}
          />
        </DataTable>
      </div>
    </FormCard>
  );
};

export default LinkUserPlant;
